import React, { forwardRef, useState, useEffect, useRef } from "react";
import HTMLFlipBook from "react-pageflip";
import { createGlobalStyle } from "styled-components";
import "../css/FlipBooks.css";
import SindhiFontWoff2 from "../fonts/SHREE-DEV7-0714.woff2";
import SindhiFontWoff from "../fonts/SHREE-DEV7-0714.woff";

const SindhiFontStyles = createGlobalStyle`
  @font-face {
    font-family: "SHREE-DEV7-0714";
    src: url(${SindhiFontWoff2}) format("woff2"),
         url(${SindhiFontWoff}) format("woff");
    font-weight: normal;
    font-style: normal;
  }

  .page-description.sindhi-text,
  .page-description.sindhi-text p,
  .page-description.sindhi-text span,
  .page-description.sindhi-text strong,
  .page-description.sindhi-text em,
  .page-description.sindhi-text u {
    font-family: "SHREE-DEV7-0714", Arial, sans-serif !important;
    font-size: 18px !important;
  }
`;

const DEBUG_FLIPBOOK = true;

const Page = forwardRef(({ children, className }, ref) => (
  <div ref={ref} className={`page ${className}`}>
    {children}
  </div>
));

const Flipbooks = ({ data, meta, forceSinglePage = false }) => {
  const [isMobile, setIsMobile] = useState(false);
  const bookRef = useRef(null);
  const currentPageRef = useRef(0); // Track the current page continuously

  // Detect screen size
  useEffect(() => {
    // if (DEBUG_FLIPBOOK) console.log("=== FLIPBOOKS DATA RECEIVED ===");
    // if (DEBUG_FLIPBOOK) console.log("Total items:", data?.length || 0);

    if (data && data.length > 0) {
      const storiesCount = data.filter(
        (item) => item.eventType === "Story"
      ).length;
      const poemsCount = data.filter(
        (item) => item.eventType === "Poem"
      ).length;
      if (DEBUG_FLIPBOOK)
        // console.log(`Stories: ${storiesCount}, Poems: ${poemsCount}`);

        // Sample a few items
        if (DEBUG_FLIPBOOK) console.log("Sample data structure:");
      data.slice(0, 5).forEach((item, index) => {
        // if (DEBUG_FLIPBOOK)
        // console.log(`Item ${index}:`, {
        //   eventType: item.eventType,
        //   name: item.eventId?.name,
        //   hasDescription: !!item.eventId?.description,
        // });
      });
    }

    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 600);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [data]);

  // Track current page continuously using onFlip
  useEffect(() => {
    if (bookRef.current?.pageFlip) {
      const api = bookRef.current.pageFlip();
      if (api && typeof api.getCurrentPageIndex === 'function') {
        currentPageRef.current = api.getCurrentPageIndex();
      }
    }
  }, [bookRef]);

  // Separate TOC pages from content pages
  const allContentPages = data.filter((item) => !item.isTableOfContents);
  const preTocPages = allContentPages.filter(
    (item) => item.eventType === "Quote" || item.eventType === "Achievement"
  );
  const contentPages = allContentPages.filter(
    (item) => item.eventType !== "Quote" && item.eventType !== "Achievement"
  );

  // if (DEBUG_FLIPBOOK) console.log("=== AFTER TOC FILTER ===");
  // if (DEBUG_FLIPBOOK)
  // console.log(`Content pages (no TOC): ${contentPages.length}`);
  // if (DEBUG_FLIPBOOK)
  //   console.log(`Pre-TOC pages: ${preTocPages.length}`);

  // if (DEBUG_FLIPBOOK) console.log("=== FINAL CONTENT PAGES ===");
  // if (DEBUG_FLIPBOOK) console.log(`Total: ${contentPages.length}`);

  // Build TOC pages first to know how many TOC pages we'll have
  const createTocPagesForCalculation = () => {
    // Filter to get only the FIRST page of each story/poem (not continuation pages)
    const stories = contentPages.filter((item) =>
      item.eventType === "Story" &&
      !item.eventId?.name?.includes("(cont.)")
    );
    const poems = contentPages.filter((item) =>
      item.eventType === "Poem" &&
      !item.eventId?.name?.includes("(cont.)")
    );
    const hasFiveWord = contentPages.some(item => item.eventType === "FiveWordStory");

    const uniqueStories = stories;
    const uniquePoems = poems;

    // Build FiveWordStory child items
    const fiveWordChildItems = [];
    if (hasFiveWord) {
      contentPages.forEach((item) => {
        if (
          item.eventType === "FiveWordStory" &&
          Array.isArray(item.tocChildTitles) &&
          item.tocChildTitles.length > 0
        ) {
          item.tocChildTitles.forEach((title) => {
            const cleanTitle = String(title)
              .replace(/\s*\(cont\.*\)$/i, "")
              .trim();
            fiveWordChildItems.push({
              type: "item",
              number: fiveWordChildItems.length + 1,
              title: cleanTitle || "No Name",
              itemType: "FiveWordStory",
              pageNumber: 0,
            });
          });
        }
      });
    }

    const allItems = [
      ...(fiveWordChildItems.length > 0 ? fiveWordChildItems : []),
      ...uniqueStories.map((story, index) => ({
        type: "item",
        number: index + 1,
        title: story.eventId?.name?.replace(/\s*\(cont\.*\)$/i, "").trim() || "No Name",
        itemType: "Story",
        pageNumber: 0,
      })),
      ...uniquePoems.map((poem, index) => ({
        type: "item",
        number: index + 1,
        title: poem.eventId?.name?.replace(/\s*\(cont\.*\)$/i, "").trim() || "No Name",
        itemType: "Poem",
        pageNumber: 0,
      })),
    ];

    const maxItemsPerPage = uniqueStories.length >= 8 ? 8 : 7;
    const totalTocPages = Math.ceil(allItems.length / maxItemsPerPage);

    return totalTocPages;
  };

  const estimatedTocPages = createTocPagesForCalculation();

  // Function to calculate ABSOLUTE page numbers for content items
  const calculatePageNumbers = () => {
    const storyPageMap = {};
    const poemPageMap = {};
    const sectionPageMap = {
      FiveWordStory: null,
    };

    // Calculate absolute page index starting from page 0
    // Structure: [0: front cover + inside front (combined as 1 spread)] [1+: preTocPages] [then: tocPages] [then: contentPages]
    const numCovers = 1; // Front cover and inside front count as 1 page in the flipbook
    const numPreToc = preTocPages.length;
    const numToc = estimatedTocPages;
    const contentOffset = numCovers + numPreToc + numToc;

    // if (DEBUG_FLIPBOOK) console.log("=== Calculating ABSOLUTE Page Numbers ===");
    // if (DEBUG_FLIPBOOK) console.log(`Content offset (absolute page index): ${contentOffset}`);
    // if (DEBUG_FLIPBOOK)
    //   console.log(`Processing ${contentPages.length} content pages`);

    contentPages.forEach((item, index) => {
      const absolutePageIndex = contentOffset + index;

      if (item.eventId && item.eventId.name && item.eventType) {
        const cleanTitle = item.eventId.name
          .replace(/\s*\(cont\.*\)$/i, "")
          .trim();
        const originalTitle = item.eventId.name;

        // if (DEBUG_FLIPBOOK)
        //   console.log(
        //     `Absolute Page ${absolutePageIndex}: "${originalTitle}" (${item.eventType})`
        //   );

        // Only assign page number to the first occurrence (not continuation pages)
        if (!originalTitle.includes("(cont.)")) {
          if (item.eventType === "Story") {
            if (!storyPageMap[cleanTitle]) {
              storyPageMap[cleanTitle] = absolutePageIndex;
              // if (DEBUG_FLIPBOOK)
              // console.log(
              //   `✓ Story "${cleanTitle}" assigned absolute page ${absolutePageIndex}`
              // );
            }
          } else if (item.eventType === "Poem") {
            if (!poemPageMap[cleanTitle]) {
              poemPageMap[cleanTitle] = absolutePageIndex;
              // if (DEBUG_FLIPBOOK)
              //   console.log(
              //     `✓ Poem "${cleanTitle}" assigned absolute page ${absolutePageIndex}`
              //   );
            }
          } else if (item.eventType === "FiveWordStory") {
            if (sectionPageMap.FiveWordStory == null) {
              sectionPageMap.FiveWordStory = absolutePageIndex;
              // if (DEBUG_FLIPBOOK)
              //   console.log(
              //     `✓ Five Word Stories section assigned absolute page ${absolutePageIndex}`
              //   );
            }
          }
        }
      }
    });

    // if (DEBUG_FLIPBOOK) console.log("Story Page Map (absolute indices):", storyPageMap);
    // if (DEBUG_FLIPBOOK) console.log("Poem Page Map (absolute indices):", poemPageMap);
    // if (DEBUG_FLIPBOOK) console.log("Section Page Map (absolute indices):", sectionPageMap);
    // if (DEBUG_FLIPBOOK) console.log("=== End Calculation ===");

    return { storyPageMap, poemPageMap, sectionPageMap };
  };

  // Calculate page numbers
  const { storyPageMap, poemPageMap, sectionPageMap } = calculatePageNumbers();

  // Build TOC pages with only Stories, Poems and Five Word Stories
  const createTocPages = () => {
    // Filter to get only the FIRST page of each story/poem (not continuation pages)
    const stories = contentPages.filter((item) =>
      item.eventType === "Story" &&
      !item.eventId?.name?.includes("(cont.)")
    );
    const poems = contentPages.filter((item) =>
      item.eventType === "Poem" &&
      !item.eventId?.name?.includes("(cont.)")
    );
    const hasFiveWord = sectionPageMap.FiveWordStory != null;

    // if (DEBUG_FLIPBOOK) console.log("=== TOC CREATION ===");
    // if (DEBUG_FLIPBOOK)
    // console.log(
    //   `Stories for TOC (excluding cont.): ${stories.length}, Poems for TOC (excluding cont.): ${poems.length}`
    // );

    const uniqueStories = stories;
    const uniquePoems = poems;

    // Build FiveWordStory child items with page numbers
    const fiveWordChildItems = [];
    if (hasFiveWord) {
      // All five word stories are on the same absolute page
      const fiveWordPage = sectionPageMap.FiveWordStory;
      contentPages.forEach((item) => {
        if (
          item.eventType === "FiveWordStory" &&
          Array.isArray(item.tocChildTitles) &&
          item.tocChildTitles.length > 0
        ) {
          item.tocChildTitles.forEach((title) => {
            const cleanTitle = String(title)
              .replace(/\s*\(cont\.*\)$/i, "")
              .trim();
            fiveWordChildItems.push({
              type: "item",
              number: fiveWordChildItems.length + 1,
              title: cleanTitle || "No Name",
              itemType: "FiveWordStory",
              pageNumber: fiveWordPage,
            });
          });
        }
      });
    }

    // if (DEBUG_FLIPBOOK)
    //   console.log(
    //     `Unique stories: ${uniqueStories.length}, Unique poems: ${uniquePoems.length}, Five word items: ${fiveWordChildItems.length}`
    //   );

    // Create separate lists with separate indexing
    const allItems = [
      // Individual Five Word Story titles if available
      ...(fiveWordChildItems.length > 0 ? fiveWordChildItems : []),
      ...uniqueStories.map((story, index) => {
        const cleanTitle =
          story.eventId?.name?.replace(/\s*\(cont\.*\)$/i, "").trim() ||
          "No Name";
        return {
          type: "item",
          number: index + 1,
          title: cleanTitle,
          itemType: "Story",
          pageNumber: storyPageMap[cleanTitle] || "",
          language: (story.eventId?.language || "").toLowerCase(),
        };
      }),
      ...uniquePoems.map((poem, index) => {
        const cleanTitle =
          poem.eventId?.name?.replace(/\s*\(cont\.*\)$/i, "").trim() ||
          "No Name";
        return {
          type: "item",
          number: index + 1,
          title: cleanTitle,
          itemType: "Poem",
          pageNumber: poemPageMap[cleanTitle] || "",
          language: (poem.eventId?.language || "").toLowerCase(),
        };
      }),
    ];

    // if (DEBUG_FLIPBOOK) console.log("TOC All Items:", allItems);

    if (allItems.length === 0) {
      return [
        {
          isTableOfContents: true,
          pageNumber: 1,
          totalTocPages: 1,
          tocItems: [],
        },
      ];
    }

    // Split into chunks with dynamic page limit based on stories count
    const maxItemsPerPage = uniqueStories.length >= 8 ? 8 : 7;
    const tocPagesArray = [];

    for (let i = 0; i < allItems.length; i += maxItemsPerPage) {
      const pageItems = allItems.slice(i, i + maxItemsPerPage);
      const pageNumber = Math.floor(i / maxItemsPerPage) + 1;
      const totalPages = Math.ceil(allItems.length / maxItemsPerPage);

      // Add section headers dynamically
      const pageItemsWithSections = [];
      let currentType = "";

      pageItems.forEach((item) => {
        if (item.itemType !== currentType) {
          let label = "";
          if (item.itemType === "Story") label = "Stories";
          else if (item.itemType === "Poem") label = "Poems";
          else if (item.itemType === "FiveWordStory")
            label = "Five Word Stories";
          pageItemsWithSections.push({ type: "header", label });
          currentType = item.itemType;
        }
        pageItemsWithSections.push(item);
      });

      tocPagesArray.push({
        isTableOfContents: true,
        pageNumber: pageNumber,
        totalTocPages: totalPages,
        tocItems: pageItemsWithSections,
      });
    }

    return tocPagesArray;
  };

  const paginatedTocPages = createTocPages();

  function goToAbsolutePage(absolutePageIndex) {
    const api = bookRef.current?.pageFlip?.();
    if (!api || absolutePageIndex == null) {
      if (DEBUG_FLIPBOOK) console.warn("No API or invalid page number", absolutePageIndex);
      return;
    }

    // absolutePageIndex is the DISPLAY page number (where numCovers = 1).
    // The actual DOM index requires numCovers = 2 (front cover + inside front).
    // Therefore, DOM index = absolutePageIndex + 1.
    const domIndex = absolutePageIndex + 1;

    // if (DEBUG_FLIPBOOK) {
    //   console.log("=== NAVIGATION DEBUG ===");
    //   console.log(`Requested display page index: ${absolutePageIndex}`);
    //   console.log(`Target DOM index: ${domIndex}`);
    //   console.log("========================");
    // }

    try {
      if (typeof api.turnToPage === "function") {
        api.turnToPage(domIndex);
      } else if (typeof api.flip === "function") {
        api.flip(domIndex);
      }
    } catch (e) {
      if (DEBUG_FLIPBOOK)
        console.error("Failed to navigate to page", domIndex, e);
    }
  }

  // Updated renderTOC with header structure matching React Native
  const renderTOC = () =>
    paginatedTocPages.map((tocPage, tocIndex) => (
      <div key={`toc-page-${tocIndex}`}>
        <Page
          onMouseEnter={() => {
            // When mouse enters this TOC page, save its index
            const numCovers = 1;
            const numPreToc = preTocPages.length;
            const tocPageIndex = numCovers + numPreToc + tocIndex;
            currentPageRef.current = tocPageIndex;
            // if (DEBUG_FLIPBOOK) console.log(`🖱️ Mouse entered TOC page ${tocPageIndex}`);
          }}
        >
          <h2>Table of Contents</h2>
          <div className="table-of-contents">
            {tocPage.tocItems.map((tocItem, itemIndex) => (
              <div key={`toc-fragment-${tocIndex}-${itemIndex}`}>
                {tocItem.type === "header" ? (
                  <div key={`toc-header-${tocIndex}-${itemIndex}`}>
                    {/* Section Title */}
                    <div
                      className="toc-section-title"
                      style={{
                        fontSize: "18px",
                        fontWeight: "bold",
                        color: "#333",
                        textAlign: "center",
                        marginBottom: "8px",
                      }}
                    >
                      {tocItem.label}
                    </div>

                    {/* Header Row - three column grid (Sr No. | Title | Page No.) */}
                    <div className="toc-header-row">
                      <span
                        className="toc-number"
                        style={{
                          fontSize: "12px",
                          fontWeight: "bold",
                          color: "#333",
                        }}
                      >
                        Sr No.
                      </span>
                      <span
                        className="toc-title"
                        style={{
                          fontSize: "12px",
                          fontWeight: "bold",
                          color: "#333",
                        }}
                      >
                        Title
                      </span>
                      <span
                        className="toc-page-number"
                        style={{
                          fontSize: "12px",
                          fontWeight: "bold",
                          color: "#333",
                        }}
                      >
                        Pg No.
                      </span>
                    </div>
                  </div>
                ) : (
                  <div
                    key={`toc-item-${tocIndex}-${itemIndex}`}
                    className="toc-row"
                    style={{ marginBottom: "8px" }}
                  >
                    <span
                      className="toc-number"
                      style={{
                        fontSize: "14px",
                        fontWeight: "bold",
                        color: "#333",
                      }}
                    >
                      {tocItem.number}.
                    </span>
                    <span
                      className="toc-title"
                      onClick={() => {
                        // Use the tracked page from ref
                        const clickedFromPage = currentPageRef.current;
                        goToAbsolutePage(tocItem.pageNumber, clickedFromPage);
                      }}
                      style={{
                        fontSize: ["sindhi", "hindi", "marathi"].includes(tocItem.language) ? "16px" : "14px",
                        fontFamily:
                          ["sindhi", "hindi", "marathi"].includes(tocItem.language)
                            ? '"SHREE-DEV7-0714", Arial, sans-serif'
                            : undefined,
                        color: "#666",
                        cursor: "pointer",
                        textDecoration: "none",
                      }}
                      onMouseEnter={(e) =>
                        (e.target.style.textDecoration = "underline")
                      }
                      onMouseLeave={(e) =>
                        (e.target.style.textDecoration = "none")
                      }
                    >
                      {tocItem.title}
                    </span>
                    <span
                      className="toc-page-number"
                      style={{
                        fontSize: "14px",
                        fontWeight: "bold",
                      }}
                    >
                      {tocItem.pageNumber}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Page>
      </div>
    ));

  const renderContentPages = () =>
    contentPages.map((item, index) => {
      // Calculate absolute page number for display
      const numCovers = 1; // Front cover and inside front are combined
      const numPreToc = preTocPages.length;
      const numToc = paginatedTocPages.length;
      const absolutePageNumber = numCovers + numPreToc + numToc + index;

      // Check if this is a continuation page
      const isContinuationPage = item.eventId?.name?.includes("(cont.)");

      const lang = (item.eventId?.language || "").toLowerCase();
      const isIndic = ["sindhi", "hindi", "marathi"].includes(lang);

      return (
        <div key={`content-fragment-${index}`}>
          <Page key={`content-${index}`}>
            {!isContinuationPage && (
              <h2
                className="page-title"
                style={
                  isIndic
                    ? {
                      fontFamily: '"SHREE-DEV7-0714", Arial, sans-serif',
                      fontSize: "22px",
                    }
                    : undefined
                }
              >
                {item.eventId ? item.eventId.name || "No Name" : "No Name"}
              </h2>
            )}
            <div
              className={`page-description${isIndic ? " sindhi-text" : ""}`}
              dangerouslySetInnerHTML={{ __html: item.eventId.description }}
            ></div>
            <footer className="page-footer">{absolutePageNumber}</footer>
          </Page>
        </div>
      );
    });

  const renderPreTocPages = () =>
    preTocPages.map((item, index) => (
      <div key={`pre-toc-fragment-${index}`}>
        <Page key={`pre-toc-${index}`}>
          <h2 className="page-title">
            {item.eventType === "Quote"
              ? "Quotes"
              : item.eventType === "Achievement"
                ? "Achievements"
                : item.eventId?.name || ""}
          </h2>
          <div
            className="page-description"
            dangerouslySetInnerHTML={{ __html: item.eventId.description }}
          ></div>
        </Page>
      </div>
    ));

  // Desktop FlipBook - Ends on Right (needs filler)
  const DesktopFlipBookEndsOnRight = ({
    renderTOC,
    renderContentPages,
    paginatedTocPages,
    contentPages,
    bookRef,
  }) => (
    <HTMLFlipBook
      ref={bookRef}
      key="desktop-right"
      width={300}
      height={500}
      showCover={true}
      mobileScrollSupport={true}
      onFlip={(e) => {
        currentPageRef.current = e.data;
        if (DEBUG_FLIPBOOK) console.log(`Page flipped to: ${e.data}`);
      }}
    >
      {/* Front hard cover */}
      <div>
        <Page className="hard">
          <h1>{meta?.bookName || "eBook"}</h1>
          {meta?.authorName ? (
            <div style={{ marginTop: "8px", fontSize: "14px" }}>
              by {meta.authorName}
            </div>
          ) : null}
        </Page>
      </div>

      {/* Inside front */}
      <div>
        <Page style={{ background: "#601945" }}>
          <h1></h1>
        </Page>
      </div>

      {/* Pre-TOC pages: Quotes and Achievements */}
      {renderPreTocPages()}

      {/* TOC pages */}
      {renderTOC()}

      {/* Content pages */}
      {renderContentPages()}
    </HTMLFlipBook>
  );

  // Desktop FlipBook - Ends on Left (has hard back cover)
  const DesktopFlipBookEndsOnLeft = ({
    renderTOC,
    renderContentPages,
    paginatedTocPages,
    contentPages,
    bookRef,
  }) => (
    <HTMLFlipBook
      ref={bookRef}
      key="desktop-left"
      width={300}
      height={500}
      showCover={true}
      mobileScrollSupport={true}
      onFlip={(e) => {
        currentPageRef.current = e.data;
        if (DEBUG_FLIPBOOK) console.log(`Page flipped to: ${e.data}`);
      }}
    >
      {/* Front hard cover */}
      <div>
        <Page className="hard">
          <h1>{meta?.bookName || "eBook"}</h1>
          {meta?.authorName ? (
            <div style={{ marginTop: "8px", fontSize: "14px" }}>
              by {meta.authorName}
            </div>
          ) : null}
        </Page>
      </div>

      {/* Inside front */}
      <div>
        <Page style={{ background: "#601945" }}>
          <h1></h1>
        </Page>
      </div>

      {/* Pre-TOC pages: Quotes and Achievements */}
      {renderPreTocPages()}

      {/* TOC pages */}
      {renderTOC()}

      {/* Content pages */}
      {renderContentPages()}

      {/* Hard back cover */}
      <div>
        <Page className="hard"></Page>
      </div>
    </HTMLFlipBook>
  );

  const MobileFlipBook = ({ bookRef }) => (
    <HTMLFlipBook
      ref={bookRef}
      key="mobile"
      width={300}
      height={500}
      showCover={false}
      mobileScrollSupport={true}
      onFlip={(e) => {
        currentPageRef.current = e.data;
        if (DEBUG_FLIPBOOK) console.log(`Page flipped to: ${e.data}`);
      }}
    >
      <div>
        <Page className="hard">
          <h1>{meta?.bookName || "eBook"}</h1>
          {meta?.authorName ? (
            <div style={{ marginTop: "8px", fontSize: "14px" }}>
              by {meta.authorName}
            </div>
          ) : null}
        </Page>
      </div>
      {/* Pre-TOC pages: Quotes and Achievements */}
      {renderPreTocPages()}
      {renderTOC()}
      {renderContentPages()}
    </HTMLFlipBook>
  );

  const totalPages =
    2 + paginatedTocPages.length + contentPages.length + preTocPages.length;
  const endsOnRight = totalPages % 2 !== 0;

  return (
    <>
      <SindhiFontStyles />
      {isMobile ? (
        <MobileFlipBook bookRef={bookRef} />
      ) : endsOnRight ? (
        <DesktopFlipBookEndsOnRight
          renderTOC={renderTOC}
          renderContentPages={renderContentPages}
          paginatedTocPages={paginatedTocPages}
          contentPages={contentPages}
          bookRef={bookRef}
        />
      ) : (
        <DesktopFlipBookEndsOnLeft
          renderTOC={renderTOC}
          renderContentPages={renderContentPages}
          paginatedTocPages={paginatedTocPages}
          contentPages={contentPages}
          bookRef={bookRef}
        />
      )}
    </>
  );
};

export default Flipbooks;